/**
 * Capacity API Service
 *
 * API functions for cloud infrastructure capacity data from ServiceMgmt API.
 * These endpoints require employee authentication (employee_uuid in JWT).
 */

import servicemgmt from '@/utils/axios/servicemgmt'
import type {
    CapacityResponse,
    ProviderCapacity,
    ZoneCapacity,
    HostCapacity,
    DatastoreCapacity,
    InstanceCapacity,
    InstanceDetail,
    InstanceMonitoring,
    ServiceMgmtResponse,
    PaginatedResponse,
} from '../types'

/**
 * Get aggregated capacity data from compute zones only
 * @returns Aggregated capacity for compute, storage, network from compute providers
 */
export const getCapacities = async (): Promise<CapacityResponse> => {
    const response = await servicemgmt.get<
        ServiceMgmtResponse<CapacityResponse>
    >('/capacities', { params: { provider_type: 'compute' } })
    return response.data.data
}

/**
 * Get capacity data for a specific provider type
 * @param providerType Provider type (e.g., 'bear' for VMware, 'panda' for OpenNebula)
 * @returns Capacity data for all zones of this provider type
 */
export const getProviderCapacity = async (
    providerType: string
): Promise<ProviderCapacity> => {
    const response = await servicemgmt.get<
        ServiceMgmtResponse<ProviderCapacity>
    >(`/capacities/provider/${providerType}`)
    return response.data.data
}

/**
 * Get capacity data for a specific zone
 * @param zoneId Zone UUID
 * @returns Capacity data for this specific zone
 */
export const getZoneCapacity = async (
    zoneId: string
): Promise<ZoneCapacity> => {
    const response = await servicemgmt.get<ServiceMgmtResponse<ZoneCapacity>>(
        `/capacities/zone/${zoneId}`
    )
    return response.data.data
}

// === Zone Resource APIs ===

/**
 * Get hosts for a specific zone
 * @param zoneId Zone UUID
 * @returns List of hosts in the zone
 */
export const getZoneHosts = async (zoneId: string): Promise<HostCapacity[]> => {
    const response = await servicemgmt.get<ServiceMgmtResponse<HostCapacity[]>>(
        `/zones/${zoneId}/hosts`
    )
    return response.data.data
}

/**
 * Get datastores for a specific zone
 * @param zoneId Zone UUID
 * @returns List of datastores in the zone
 */
export const getZoneDatastores = async (
    zoneId: string
): Promise<DatastoreCapacity[]> => {
    const response = await servicemgmt.get<
        ServiceMgmtResponse<DatastoreCapacity[]>
    >(`/zones/${zoneId}/datastores`)
    return response.data.data
}

/**
 * Get instances for a specific zone (from capacity API - live provider data)
 * @param zoneId Zone UUID
 * @returns List of instances in the zone
 */
export const getZoneInstances = async (
    zoneId: string
): Promise<InstanceCapacity[]> => {
    const response = await servicemgmt.get<ServiceMgmtResponse<ZoneCapacity>>(
        `/capacities/zone/${zoneId}`
    )
    return response.data.data.instances || []
}

// === Instance APIs ===

/** Parameters for listing instances */
export interface ListInstancesParams {
    zone_id?: string
    status?: string
    name?: string
    offset?: number
    limit?: number
}

/**
 * Get all instances (cross-zone) with pagination
 * @param params Optional filter/pagination params
 * @returns Paginated list of instances
 */
export const getAllInstances = async (
    params?: ListInstancesParams
): Promise<{ data: InstanceCapacity[]; total: number }> => {
    const response = await servicemgmt.get<PaginatedResponse<InstanceCapacity>>(
        '/instances',
        { params }
    )
    return { data: response.data.data, total: response.data.total }
}

/**
 * Get single instance detail
 * @param id Instance UUID
 * @returns Instance detail with zone/provider info
 */
export const getInstance = async (id: string): Promise<InstanceDetail> => {
    const response = await servicemgmt.get<ServiceMgmtResponse<InstanceDetail>>(
        `/instances/${id}`
    )
    return response.data.data
}

/**
 * Get instance monitoring data
 * @param id Instance UUID
 * @param from Optional start time (ISO string)
 * @param to Optional end time (ISO string)
 * @returns Array of monitoring data points
 */
export const getInstanceMonitoring = async (
    id: string,
    from?: string,
    to?: string
): Promise<InstanceMonitoring[]> => {
    const response = await servicemgmt.get<
        ServiceMgmtResponse<InstanceMonitoring[]>
    >(`/instances/${id}/monitoring`, { params: { from, to } })
    return response.data.data
}
